import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNotify } from '@hotjar/webapp/hooks/useNotify';

import { ManageCollectionModal } from './ManageCollectionModal';
import { useCollectionRename, ExistingCollectionError } from './use-collection-rename';

interface RenameCollectionProps {
  collectionId: string;
  collectionName: string;
  onClose: () => void;
  siteId: string;
}

export function RenameCollection({
  collectionId,
  collectionName,
  onClose,
  siteId,
}: RenameCollectionProps): JSX.Element {
  const { t } = useTranslation();
  const { mutateAsync: rename, status } = useCollectionRename(siteId, collectionId);
  const { success, error } = useNotify();
  const [validationError, setValidationError] = useState<string | null>(null);
  const resetValidationError = useCallback(() => {
    setValidationError(null);
  }, [setValidationError]);
  const handleConfirm = useCallback(
    async (updatedCollectionName) => {
      resetValidationError();

      try {
        await rename({ name: updatedCollectionName });
        success(t('collections:rename.success'));
        onClose();
      } catch (err) {
        if (err instanceof ExistingCollectionError) {
          setValidationError(t('collections:rename.error.existing'));

          return;
        }

        error(t('collections:rename.error.unknown'));
      }
    },
    [error, onClose, rename, resetValidationError, success, t],
  );

  return (
    <ManageCollectionModal
      error={validationError ?? undefined}
      editedCollection={{ name: collectionName }}
      isLoading={status === 'loading'}
      isOpen
      loadingText={t('collections:rename.progress')}
      onCancel={onClose}
      onConfirm={handleConfirm}
      resetError={resetValidationError}
    />
  );
}
